import { Link } from "react-router-dom";
import { Award, Users, Heart, Shield, ArrowRight, Phone } from "lucide-react";
import SEO from "../components/SEO";
import { ASSETS, BUSINESS } from "../lib/data";

export default function ChiSiamo() {
  return (
    <>
      <SEO
        title="Chi Siamo | Il Velocipede - Storico rivenditore ufficiale Piaggio Roma dal 1973"
        description="La storia del Velocipede: dal 1973 punto di riferimento nel quartiere Nuovo Salario di Roma per scooter, moto e biciclette. Esperienza, fiducia e assistenza qualificata."
      />

      {/* HERO */}
      <section className="relative bg-[#013368] text-white overflow-hidden" data-testid="chisiamo-hero">
        <div className="absolute inset-0 opacity-25">
          <img src={ASSETS.urbanRome} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="text-[#fd0100] text-xs uppercase tracking-[0.22em] font-semibold">Chi Siamo</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Una storia lunga più di mezzo secolo
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-200 leading-relaxed">
              Dal 1973, Il Velocipede è il punto di riferimento per le due ruote nel quartiere Nuovo Salario di Roma. Una storia di persone, di mezzi e di una passione che non è mai cambiata.
            </p>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline">La nostra storia</div>
            <div className="divider-red mt-3 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Dal 1973, a fianco di chi sceglie le due ruote
            </h2>
            <div className="mt-8 space-y-5 text-slate-600 leading-relaxed text-base">
              <p>
                Il Velocipede apre i battenti nel <strong>1973</strong> in Piazza dell'Ateneo Salesiano, nel cuore del quartiere Nuovo Salario. In oltre cinquant'anni di attività abbiamo visto cambiare Roma, abbiamo accompagnato l'evoluzione degli scooter e siamo cresciuti insieme alle famiglie del quartiere.
              </p>
              <p>
                Siamo diventati <strong>rivenditore ufficiale Piaggio</strong> raccogliendo l'eredità di un brand che ha letteralmente motorizzato l'Italia. Negli anni abbiamo aggiunto la <strong>rappresentanza ufficiale Kymco</strong>, ampliando la gamma per offrire ai clienti tutta la scelta che meritano.
              </p>
              <p>
                La nostra <strong>officina specializzata</strong> è il vero cuore pulsante dell'attività: meccanici qualificati che lavorano con strumenti diagnostici di ultima generazione, ricambi originali e — soprattutto — quella cura per il dettaglio che si tramanda solo in chi questo mestiere lo ha respirato fin da piccolo.
              </p>
              <p>
                Oggi, come ieri, ogni cliente che varca la nostra porta trova lo stesso approccio: <strong>ascolto, competenza, trasparenza</strong>. Perché un mezzo a due ruote non è mai solo un mezzo — è libertà, lavoro, ricordi.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28 grid grid-cols-2 gap-4">
              <div className="col-span-2 rounded-2xl overflow-hidden aspect-[16/10]">
                <img src={ASSETS.storefront} alt="Il Velocipede negozio Roma Nuovo Salario" className="w-full h-full object-cover" />
              </div>
              <ValueChip number="1973" label="Anno di fondazione" />
              <ValueChip number="52+" label="Anni di esperienza" />
              <ValueChip number="2" label="Brand ufficiali" />
              <ValueChip number="100%" label="Ricambi originali" />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="overline">I nostri valori</div>
            <div className="divider-red mt-3 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Quello in cui crediamo
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <ValueCard icon={Award} title="Esperienza" desc="Cinque decenni di lavoro quotidiano sulle due ruote, dai motori a due tempi all'elettrico." />
            <ValueCard icon={Users} title="Rapporto umano" desc="Il cliente non è un numero: è una persona con un mezzo, una storia, un'esigenza specifica." />
            <ValueCard icon={Shield} title="Trasparenza" desc="Preventivi chiari, lavori spiegati, nessuna sorpresa in fattura. Sempre." />
            <ValueCard icon={Heart} title="Passione" desc="Lo facciamo perché ci piace farlo. E si vede." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#013368] rounded-3xl p-10 md:p-16 text-center text-white">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Vieni a conoscerci di persona
          </h2>
          <p className="mt-4 text-slate-200 max-w-xl mx-auto">
            Passa in negozio o chiamaci: ti accoglieremo con la stessa cura con cui lo facciamo dal 1973.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="chisiamo-cta-call" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Phone className="w-4 h-4" /> Chiama Ora
            </a>
            <Link to="/contatti" data-testid="chisiamo-cta-contatti" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white text-[#013368] hover:bg-slate-100 transition">
              Vai ai contatti <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function ValueChip({ number, label }) {
  return (
    <div className="bg-[#013368] text-white p-5 rounded-2xl text-center">
      <div className="font-display font-extrabold text-3xl">{number}</div>
      <div className="mt-1 text-xs text-slate-300 leading-tight">{label}</div>
    </div>
  );
}

function ValueCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#013368] transition-colors">
      <div className="w-12 h-12 rounded-xl bg-[#e6eff8] flex items-center justify-center mb-5">
        <Icon className="w-6 h-6 text-[#013368]" />
      </div>
      <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
      <p className="mt-2 text-slate-600 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
